/**
 * The text player's answer field (ADR-116 Decision 8; handoff §11.2, §19.8).
 *
 * A self-contained field with no player logic: it owns typing, the keyboard and its
 * own look, and reports `onChange` / `onSubmit`. It is a **known second copy** of
 * techniques that live inline in `LearnPage.tsx`, ported by copy as ADR-116 Decision 8
 * requires (ADR-116 Implementation notes 3 and 4). Learn's field is not touched and
 * nothing is shared with it; unifying the two is a later decision that needs its own ADR.
 *
 * Two variants, chosen by the caller from the device, not the width:
 *
 * **`soft` (a touch keyboard; the phone, and any touch tablet).** Ported, with the records
 * they come from:
 * - **Prediction suppression (ADR-014 §8 and its 2026-07-10 / 2026-07-16 amendments).**
 *   `type="password"` is the load-bearing signal: the only reliable way to keep Gboard,
 *   Samsung Keyboard and SwiftKey from showing suggestions, autocorrecting or learning.
 *   Around it: `inputMode="text"`, `lang="zxx"`, `autoComplete="one-time-code"`,
 *   autocorrect, autocapitalize and spellcheck off, the password-manager and Grammarly
 *   opt-outs, a randomized non-form `name`, and no enclosing `<form>`.
 * - **Drawn letters and a synthetic caret (ADR-014 §9 and its caret amendment).** A
 *   password field paints bullets, so the input's own text and caret are transparent
 *   and an overlay draws the letters and a thin caret at the measured position. Taps
 *   are mapped to a character through the same glyph measurement, and a range
 *   selection is collapsed at once (it would paint bullets and open a menu).
 * - **The keyboard stays up (ADR-011, ADR-013 §1/§7, ADR-014 §6).** Never `readOnly`;
 *   never a `setTimeout` refocus. While `keepFocus` is on, a blur that nobody asked for
 *   is undone in the same tick, and a viewport that grows by more than 80 px with the
 *   field unfocused (the system closed the keyboard) refocuses it synchronously.
 *
 * **`physical` (a mouse and a keyboard: desktop Chrome, Edge, Firefox).** There is no
 * soft keyboard to suppress, and `type="password"` would invite the browser's password
 * manager (a save prompt, an autofill list, a key icon). So this variant is a plain
 * `type="text"` input with the browser's own text, caret and selection, and no password
 * field exists on the page for a manager to act on. `autoComplete="off"`, a random
 * non-form `name`, the third-party opt-outs and no enclosing `<form>` remain. Focus is
 * kept by the page (nothing else takes it on a click), not by undoing blurs: Tab and
 * the sidebar keep working. ↑ and ↓ in the field move between gaps (handoff §19.8).
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Check } from 'lucide-react'

export type AnswerTone = 'idle' | 'correct' | 'fits' | 'wrong' | 'neutral'
export type AnswerVariant = 'soft' | 'physical'

export interface AnswerFieldHandle {
  /** Focus synchronously (call from a tap handler so Android raises the keyboard). */
  focus: () => void
  /** Blur on purpose (a sheet is opening): the recovery does not undo it. */
  release: () => void
}

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  tone: AnswerTone
  ariaLabel: string
  submitLabel: string
  /** Part of the randomized `name`, so browsers keep no history for it. */
  nameKey: string
  /** Undo unwanted blurs and keyboard retracts (soft only; off while a sheet or summary is up). */
  keepFocus: boolean
  variant?: AnswerVariant
  /** Physical only: ↑ / ↓ pressed in the field. */
  onArrow?: (dir: 'up' | 'down') => void
  testId?: string
}

const GEOMETRY = {
  soft: { font: 19, padLeft: 14, padRight: 48, height: 48, button: 36 },
  physical: { font: 20, padLeft: 16, padRight: 52, height: 52, button: 38 },
} as const

const TONE_BORDER: Record<AnswerTone, string> = {
  idle: 'var(--accent)',
  correct: 'var(--success)',
  fits: 'var(--accent)',
  wrong: 'var(--danger)',
  neutral: 'var(--border)',
}

const PHYSICAL_CSS = '.answer-field-physical::placeholder{color:var(--text-tertiary);opacity:1}'

const AnswerField = forwardRef<AnswerFieldHandle, Props>(function AnswerField(
  { value, onChange, onSubmit, placeholder, tone, ariaLabel, submitLabel, nameKey, keepFocus, variant = 'soft', onArrow, testId },
  ref,
) {
  const g = GEOMETRY[variant]
  const soft = variant === 'soft'
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [focused, setFocused] = useState(false)
  const [caretIndex, setCaretIndex] = useState<number | null>(null)
  const [scrollLeft, setScrollLeft] = useState(0)
  const intentionalBlur = useRef(false)
  const keepFocusRef = useRef(keepFocus)
  keepFocusRef.current = keepFocus
  // One random suffix per mounted field (ADR-014 §8: defeats saved history).
  const [salt] = useState(() => Math.random().toString(36).slice(2, 8))

  useImperativeHandle(ref, () => ({
    focus: () => {
      const el = inputRef.current
      if (!el) return
      el.focus({ preventScroll: true })
      const len = el.value.length
      try { el.setSelectionRange(len, len) } catch { /* harmless */ }
    },
    release: () => {
      intentionalBlur.current = true
      inputRef.current?.blur()
    },
  }), [])

  // ── Glyph measurement: one source for the caret and the tap mapping (soft) ───
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const measure = useCallback((text: string): number => {
    if (!text) return 0
    const c = canvas.current ?? (canvas.current = document.createElement('canvas'))
    const ctx = c.getContext('2d')
    if (!ctx) return 0
    const family = getComputedStyle(document.documentElement).getPropertyValue('--font-content').trim() || 'serif'
    ctx.font = `400 ${g.font}px ${family}`
    return ctx.measureText(text).width
  }, [g.font])

  const syncCaret = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    setCaretIndex(el.selectionStart)
    setScrollLeft(el.scrollLeft)
  }, [])

  const indexForClientX = useCallback((clientX: number, el: HTMLInputElement): number => {
    const rect = el.getBoundingClientRect()
    const target = clientX - rect.left - g.padLeft + el.scrollLeft
    let best = 0
    let bestDist = Infinity
    for (let i = 0; i <= value.length; i++) {
      const d = Math.abs(measure(value.slice(0, i)) - target)
      if (d < bestDist) { bestDist = d; best = i }
    }
    return best
  }, [value, measure, g.padLeft])

  const onTap = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    if (!soft) return
    const el = inputRef.current
    if (!el) return
    const at = indexForClientX(e.clientX, el)
    try { el.setSelectionRange(at, at) } catch { /* ignore */ }
    setCaretIndex(at)
    setScrollLeft(el.scrollLeft)
  }, [soft, indexForClientX])

  const lastPress = useRef<{ x: number; t: number } | null>(null)
  const collapsing = useRef(false)
  const collapseRange = useCallback((): boolean => {
    if (collapsing.current) return false
    const el = inputRef.current
    if (!el) return false
    const { selectionStart: s, selectionEnd: e } = el
    if (s == null || e == null || s === e) return false
    collapsing.current = true
    try {
      const press = lastPress.current
      const target = press && performance.now() - press.t < 1500 ? indexForClientX(press.x, el) : e
      try { el.setSelectionRange(target, target) } catch { /* ignore */ }
      setCaretIndex(target)
      setScrollLeft(el.scrollLeft)
    } finally {
      collapsing.current = false
    }
    return true
  }, [indexForClientX])

  const onSelection = useCallback(() => {
    if (!soft) return
    if (!collapseRange()) syncCaret()
  }, [soft, collapseRange, syncCaret])

  useEffect(() => {
    if (!soft || !focused) return
    document.addEventListener('selectionchange', onSelection)
    return () => document.removeEventListener('selectionchange', onSelection)
  }, [soft, focused, onSelection])

  // ── Keyboard stays up (soft) ────────────────────────────────────────────────
  const onBlur = useCallback(() => {
    setFocused(false)
    if (!soft) return
    if (intentionalBlur.current) { intentionalBlur.current = false; return }
    if (!keepFocusRef.current) return
    const el = inputRef.current
    if (!el) return
    const { selectionStart, selectionEnd } = el
    el.focus({ preventScroll: true })
    try { el.setSelectionRange(selectionStart ?? el.value.length, selectionEnd ?? el.value.length) } catch { /* ignore */ }
  }, [soft])

  const [viewportH, setViewportH] = useState(() => window.visualViewport?.height ?? window.innerHeight)
  useEffect(() => {
    if (!soft) return
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setViewportH(vv.height)
    vv.addEventListener('resize', update)
    return () => vv.removeEventListener('resize', update)
  }, [soft])
  const prevViewport = useRef(0)
  useLayoutEffect(() => {
    const prev = prevViewport.current
    prevViewport.current = viewportH
    if (!soft || prev === 0 || viewportH <= prev + 80 || !keepFocusRef.current) return
    const el = inputRef.current
    if (!el || document.activeElement === el) return
    el.focus({ preventScroll: true })
    const len = el.value.length
    try { el.setSelectionRange(len, len) } catch { /* ignore */ }
  }, [soft, viewportH])

  const caretPx = useMemo(() => {
    if (!focused) return 0
    const at = caretIndex == null ? value.length : Math.min(caretIndex, value.length)
    return g.padLeft + measure(value.slice(0, at)) - scrollLeft
  }, [focused, caretIndex, value, scrollLeft, measure, g.padLeft])

  const empty = value.length === 0
  const text: React.CSSProperties = {
    fontSize: g.font, fontFamily: 'var(--font-content)', fontWeight: 400, lineHeight: 1.4,
  }

  // The attributes both variants share: no autofill history, no third-party helpers.
  const shared = {
    lang: 'zxx',
    inputMode: 'text' as const,
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: false,
    enterKeyHint: 'done' as const,
    'aria-label': ariaLabel,
    'aria-autocomplete': 'none' as const,
    'data-form-type': 'other',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-gramm': 'false',
    'data-gramm_editor': 'false',
    'data-enable-grammarly': 'false',
    name: `text-answer-${nameKey}-${salt}`,
    value,
  }

  return (
    <div
      data-testid={testId}
      style={{
        position: 'relative', flex: '1 1 0', minWidth: 0, height: g.height, boxSizing: 'border-box',
        borderRadius: 16, border: `2px solid ${TONE_BORDER[tone]}`, background: 'var(--bg-surface)',
        transition: 'border-color 0.15s',
      }}
    >
      {soft ? (
        <>
          {/* The visible letters and caret: the input below paints only bullets. */}
          <div
            aria-hidden
            style={{
              ...text, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              paddingLeft: g.padLeft, paddingRight: g.padRight, whiteSpace: 'pre', overflow: 'hidden', pointerEvents: 'none',
              color: empty ? 'var(--text-tertiary)' : 'var(--text-primary)',
            }}
          >
            <span style={{ transform: empty ? undefined : `translateX(${-scrollLeft}px)`, flexShrink: 0 }}>
              {empty ? placeholder : value}
            </span>
            {focused && (
              <span
                data-testid="answer-caret"
                style={{
                  position: 'absolute', left: caretPx, top: '50%', transform: 'translateY(-50%)',
                  width: 2, height: '1.1em', background: 'var(--accent)',
                }}
              />
            )}
          </div>
          <input
            ref={inputRef}
            {...shared}
            type="password"
            autoComplete="one-time-code"
            data-testid="answer-input"
            onChange={(e) => { onChange(e.target.value); syncCaret() }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey) return
              e.preventDefault()
              e.stopPropagation()
              onSubmit()
            }}
            onPointerDown={(e) => { lastPress.current = { x: e.clientX, t: performance.now() } }}
            onClick={onTap}
            onSelect={onSelection}
            onKeyUp={onSelection}
            onScroll={syncCaret}
            onFocus={() => { setFocused(true); syncCaret() }}
            onBlur={onBlur}
            style={{
              ...text, width: '100%', height: '100%', boxSizing: 'border-box', margin: 0,
              paddingLeft: g.padLeft, paddingRight: g.padRight, border: 'none', outline: 'none', borderRadius: 14,
              background: 'transparent', color: 'transparent', caretColor: 'transparent',
            }}
          />
        </>
      ) : (
        <>
          <style>{PHYSICAL_CSS}</style>
          <input
            ref={inputRef}
            {...shared}
            type="text"
            autoComplete="off"
            className="answer-field-physical"
            data-testid="answer-input"
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                e.stopPropagation()
                onSubmit()
              } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && onArrow && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                // A single line has no use for them: they move between gaps (§19.8).
                e.preventDefault()
                onArrow(e.key === 'ArrowUp' ? 'up' : 'down')
              }
            }}
            onFocus={() => setFocused(true)}
            onBlur={onBlur}
            style={{
              ...text, width: '100%', height: '100%', boxSizing: 'border-box', margin: 0,
              paddingLeft: g.padLeft, paddingRight: g.padRight, border: 'none', outline: 'none', borderRadius: 14,
              background: 'transparent', color: 'var(--text-primary)', caretColor: 'var(--accent)',
            }}
          />
        </>
      )}
      {/* Inside on the right; never takes focus, so the field keeps it (ADR-013). */}
      <button
        type="button"
        aria-label={submitLabel}
        data-testid="answer-submit"
        onPointerDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onSubmit}
        style={{
          position: 'absolute', right: soft ? 4 : 6, top: '50%', transform: 'translateY(-50%)',
          width: g.button, height: g.button, minWidth: g.button, minHeight: g.button, borderRadius: '50%', border: 'none', padding: 0,
          WebkitAppearance: 'none', appearance: 'none', cursor: 'pointer',
          background: 'var(--accent-fill)', color: 'var(--text-on-accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Check size={18} aria-hidden />
      </button>
    </div>
  )
})

export default AnswerField
